/**
 * context-engine.js — RLM + PaTH-FoX Inspired Context Scaffolding
 */

interface Message {
    role: string;
    content: string | any[];
    index: number;
    timestamp: number;
}

interface Chunk extends Message {
    chunkContent: string;
    subIndex?: number;
    score?: number;
}

class ContextEngine {
    public store: Message[] = [];
    private topK: number;
    private recentAnchor: number;
    private maxContext: number;
    private decayBase: number;
    private recencyWeight: number;

    constructor({
        topK = 5,
        recentAnchor = 3,
        maxContext = 40,
        decayBase = 0.92,
        recencyWeight = 0.25
    } = {}) {
        this.topK = topK;
        this.recentAnchor = recentAnchor;
        this.maxContext = maxContext;
        this.decayBase = decayBase;
        this.recencyWeight = recencyWeight;
    }

    // ── Store Management ────────────────────────────────────────────────

    push(role: string, content: any) {
        if (!content) return;
        const finalContent = typeof content === 'string' ? content.trim() : content;
        if (!finalContent) return;

        this.store.push({
            role,
            content: finalContent,
            index: this.store.length,
            timestamp: Date.now()
        });
    }

    hydrate(messages: any[]) {
        this.store = [];
        messages.forEach((m, i) => {
            let content = m.content;
            // Attempt to parse if it looks like JSON array (multi-modal)
            if (typeof content === 'string' && content.startsWith('[') && content.endsWith(']')) {
                try { content = JSON.parse(content); } catch(e) {}
            }

            this.store.push({
                role: m.role,
                content: typeof content === 'string' ? content.trim() : content,
                index: i,
                timestamp: i
            });
        });
    }

    clear() {
        this.store = [];
    }

    // ── Stop Words ──────────────────────────────────────────────────────

    static STOP_WORDS = new Set([
        'the', 'a', 'an', 'is', 'it', 'in', 'on', 'at', 'to', 'for', 'of', 'and', 'or',
        'but', 'not', 'be', 'are', 'was', 'were', 'i', 'you', 'he', 'she', 'we', 'they',
        'this', 'that', 'with', 'has', 'have', 'had', 'do', 'did', 'can', 'will', 'would',
        'could', 'should', 'my', 'your', 'its', 'our', 'their', 'what', 'how', 'when',
        'where', 'why', 'which', 'who', 'from', 'by', 'as', 'so', 'if', 'then', 'just',
        'about', 'please', 'help', 'tell', 'me', 'want', 'need', 'make', 'get', 'use',
        'just', 'also', 'any', 'some', 'all', 'more', 'new', 'very', 'been', 'than'
    ]);

    private _tokenize(text: string): string[] {
        if (typeof text !== 'string') return [];
        return text
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, ' ')
            .split(/\s+/)
            .filter(w => w.length > 2 && !ContextEngine.STOP_WORDS.has(w));
    }

    private _extractKeywords(text: string): Set<string> {
        return new Set(this._tokenize(text));
    }

    // ── Chunking ────────────────────────────────────────────────────────

    private _chunkMessage(msg: Message): Chunk[] {
        const MAX_CHUNK_LENGTH = 800;
        let textContent = "";

        if (typeof msg.content === 'string') {
            textContent = msg.content;
        } else if (Array.isArray(msg.content)) {
            // Extract all text parts for chunking/indexing
            textContent = msg.content
                .filter((c: any) => c.type === 'text')
                .map((c: any) => c.text)
                .join(" ");
        }

        if (!textContent || textContent.length <= MAX_CHUNK_LENGTH) {
            return [{ ...msg, chunkContent: textContent || "" }];
        }

        return textContent
            .split(/\n\s*\n/)
            .map((para, i) => ({
                ...msg,
                chunkContent: para.trim(),
                subIndex: i
            }))
            .filter(c => c.chunkContent.length > 10);
    }

    private _getAllChunks(): Chunk[] {
        const chunks: Chunk[] = [];
        for (const msg of this.store) {
            for (const chunk of this._chunkMessage(msg)) {
                chunks.push(chunk);
            }
        }
        return chunks;
    }

    // ── Scoring (PaTH-FoX Inspired) ─────────────────────────────────────

    private _keywordScore(keywords: Set<string>, chunkContent: string): number {
        if (keywords.size === 0) return 0;
        const chunkWords = new Set(this._tokenize(chunkContent));
        let matches = 0;
        for (const kw of keywords) {
            if (chunkWords.has(kw)) {
                matches += 1.0;
            } else {
                for (const cw of chunkWords) {
                    if (cw.includes(kw) || kw.includes(cw)) {
                        matches += 0.5;
                        break;
                    }
                }
            }
        }
        return matches / keywords.size;
    }

    private _recencyDecay(chunkIndex: number, totalMessages: number): number {
        const distance = totalMessages - 1 - chunkIndex;
        return Math.pow(this.decayBase, distance);
    }

    private _entityPathBoost(keywords: Set<string>, chunkContent: string, allChunks: Chunk[], chunkIndex: number): number {
        const chunkWords = new Set(this._tokenize(chunkContent));
        const sharedWithQuery = [...keywords].filter(k => chunkWords.has(k));
        if (sharedWithQuery.length === 0) return 0;

        const laterChunksWithSameEntities = allChunks.slice(chunkIndex + 1).some(later => {
            const laterWords = new Set(this._tokenize(later.chunkContent));
            return sharedWithQuery.some(k => laterWords.has(k));
        });

        return laterChunksWithSameEntities ? 0.2 : 0;
    }

    private _scoreChunk(keywords: Set<string>, chunk: Chunk, allChunks: Chunk[], totalMessages: number): number {
        const kScore = this._keywordScore(keywords, chunk.chunkContent);
        const decay = this._recencyDecay(chunk.index, totalMessages);
        const entityBoost = kScore > 0
            ? this._entityPathBoost(keywords, chunk.chunkContent, allChunks, chunk.index)
            : 0;

        const baseScore = (kScore * (1 - this.recencyWeight)) + (decay * this.recencyWeight);
        return baseScore + entityBoost;
    }

    // ── RLM Environmental Methods ───────────────────────────────────────

    getEnvironmentSnapshot(): string {
        const total = this.store.length;
        if (total === 0) return "Global Environment: History is empty.";

        let snapshot = `Global Environment: ${total} messages stored.\n`;
        snapshot += `Indexes 0 to ${Math.max(0, total - 1)} available.\n\n`;
        snapshot += "History Mapping (Sampled):\n";

        const step = total > 30 ? Math.ceil(total / 10) : 1;

        for (let i = 0; i < total; i += step) {
            const msg = this.store[i];
            let preview = "";
            if (typeof msg.content === 'string') {
                preview = msg.content.substring(0, 60);
            } else if (Array.isArray(msg.content)) {
                const hasImage = msg.content.some((c: any) => c.type === 'image_url');
                const textPart = msg.content.find((c: any) => c.type === 'text')?.text || "";
                preview = `${hasImage ? '[IMAGE] ' : ''}${textPart.substring(0, 50)}`;
            }
            preview = preview.replace(/\n/g, ' ');
            snapshot += `[MSG #${msg.index}] Role: ${msg.role} | Snippet: "${preview}..."\n`;
        }

        if (total > this.recentAnchor) {
            snapshot += `\nLatest Anchor: Messages #${total - this.recentAnchor} to #${total - 1} are currently "active" in your immediate window.`;
        }

        return snapshot;
    }

    searchHistory(query: string, customTopK = 10): any[] {
        const keywords = this._extractKeywords(query);
        const allChunks = this._getAllChunks();

        const matches = allChunks
            .map(c => ({
                ...c,
                score: this._keywordScore(keywords, c.chunkContent)
            }))
            .filter(c => c.score > 0.1)
            .sort((a, b) => b.score! - a.score!);

        const selectedIndices = new Set();
        const results: any[] = [];
        for (const match of matches) {
            if (selectedIndices.has(match.index)) continue;
            if (results.length >= customTopK) break;
            selectedIndices.add(match.index);
            results.push({
                index: match.index,
                role: match.role,
                content: match.content
            });
        }

        return results;
    }

    async getEnvironmentalSnapshot(): Promise<string> {
        const total = this.store.length;
        const now = new Date();
        const timestamp = now.toLocaleString();
        
        let snapshot = `### ENVIRONMENT SNAPSHOT (${timestamp})\n`;
        snapshot += `- **Messages in Memory**: ${total}\n`;
        snapshot += `- **Active Context Window**: Last ${this.recentAnchor} msgs + top-${this.topK} relevant blocks.\n`;
        
        const projectRules = localStorage.getItem('ai-tools-project-rules');
        if (projectRules) {
            snapshot += `\n### PROJECT RULES\n${projectRules}\n`;
        }

        return snapshot;
    }

    getMessagesByIndices(indices: number[]): any[] {
        return this.store
            .filter(m => indices.includes(m.index))
            .map(m => ({ index: m.index, role: m.role, content: m.content }));
    }

    // ── Context Assembly ────────────────────────────────────────────────

    private stripImagesFromContent(content: any): any {
        if (typeof content === 'string') return content;
        if (Array.isArray(content)) {
            const textPart = content.find((c: any) => c.type === 'text');
            if (textPart) return textPart.text;
            const imgUrlPart = content.find((c: any) => c.type === 'image_url');
            if (imgUrlPart) return '[Image attached - not shown]';
            return '[Image attached - not shown]';
        }
        return content;
    }

    assembleContext(query: string): any[] {
        const total = this.store.length;
        if (total === 0) return [];

        const recentStart = Math.max(0, total - this.recentAnchor);
        const recentIndices = new Set(
            this.store.slice(recentStart).map(m => m.index)
        );

        const keywords = this._extractKeywords(query);
        const allChunks = this._getAllChunks();

        const scoredChunks = allChunks
            .filter(c => !recentIndices.has(c.index))
            .map(c => ({
                ...c,
                score: this._scoreChunk(keywords, c, allChunks, total)
            }))
            .filter(c => c.score > 0.05)
            .sort((a, b) => b.score - a.score);

        const selectedIndices = new Set();
        const topChunks: any[] = [];
        for (const chunk of scoredChunks) {
            if (selectedIndices.has(chunk.index)) continue;
            if (topChunks.length >= this.topK) break;
            selectedIndices.add(chunk.index);
            topChunks.push(chunk);
        }

        const allSelectedIndices = new Set([...selectedIndices, ...recentIndices]);

        const finalContext = this.store
            .filter(m => allSelectedIndices.has(m.index))
            .slice(-this.maxContext)
            .map(m => ({
                role: m.role === 'assistant' ? 'assistant' : 'user',
                content: this.stripImagesFromContent(m.content),
                type: 0
            }));

        return finalContext;
    }
}

// Global singleton
const contextEngine = new ContextEngine({
    topK: 6,
    recentAnchor: 3,
    maxContext: 50,
    decayBase: 0.93,
    recencyWeight: 0.2
});

(window as any).contextEngine = contextEngine;
export { contextEngine };
